/**
 * @file AuditGroupCard.tsx
 * @description Группа замечаний «Проверки сайта» (phase19.16, партия Р3в,
 * v0.19.64; SB132; uiux-r3 §3.5): значок уровня (! предупреждение, ⓘ
 * сведение), заголовок и подсказка из shared `SITE_SEO_ISSUES`, строки с
 * пояснением и «Исправить →» — ссылкой в место правки. Длинный список —
 * первые 20 строк и «Показать все N». Ссылка — 48 px до 1024.
 */

import { Info, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import type { AuditGroup } from "../../../lib/site-seo/audit-view";

/** Сколько строк группы показывать сразу. */
const ROWS_SHOWN = 20;

const FIX_LINK = "type-body inline-flex min-h-12 shrink-0 items-center font-medium text-primary hover:underline lg:min-h-0";

export function AuditGroupCard({ group }: { group: AuditGroup }) {
  const [all, setAll] = useState(false);
  const warning = group.level === "warning";
  const Icon = warning ? TriangleAlert : Info;
  const rows = all ? group.rows : group.rows.slice(0, ROWS_SHOWN);
  return (
    <section className="flex min-w-0 flex-col gap-2 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]" data-testid={`audit-group-${group.code}`}>
      <div className="flex min-w-0 items-start gap-2">
        <Icon aria-hidden className={warning ? "mt-0.5 size-5 shrink-0 text-warning" : "mt-0.5 size-5 shrink-0 text-info"} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="type-body font-semibold text-on-surface">
            <span className="sr-only">{warning ? "Предупреждение: " : "Сведение: "}</span>
            {group.title} <span className="font-normal text-on-surface-variant">· {group.rows.length}</span>
          </h3>
          {group.hint ? <p className="type-caption text-on-surface-variant">{group.hint}</p> : null}
        </div>
      </div>
      <ul className="flex flex-col divide-y divide-[var(--outline-variant)] pl-7">
        {rows.map((row) => (
          <li key={row.key} className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 py-1">
            <span className="type-body min-w-0 break-words text-on-surface">
              {row.title}
              {row.note ? <span className="text-on-surface-variant"> · {row.note}</span> : null}
            </span>
            {row.href ? (
              <Link to={row.href} className={FIX_LINK}>
                Исправить →
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
      {group.rows.length > ROWS_SHOWN && !all ? (
        <Button variant="link" className="min-h-12 self-start pl-7 lg:min-h-0" onClick={() => setAll(true)}>
          Показать все {group.rows.length}
        </Button>
      ) : null}
    </section>
  );
}
